import { Css } from "@homebound/beam";
import { CoverImage, Icon } from "src/components";
import {
  SideNavAndRouterConfig,
  SideNavAndRouterConfigItem,
  SideNavDisplayStyle,
  SideNavProps,
} from "src/components/layout/SideNav";
import { FeatureFlagType, ProjectFeature, Stage } from "src/generated/graphqlTypes";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import {
  createBidPackagesUrl,
  createBudgetMetricsUrl,
  createBudgetUrl,
  createChangeEventsUrl,
  createCommitmentsUrl,
  createDocumentsUrl,
  createDraftScheduleUrl,
  createEstimatesUrl,
  createHomeownerContractsUrl,
  createInvoicesUrl,
  createPreConServicesUrl,
  createProductOfferingsUrl,
  createProjectBidContractsUrl,
  createProjectChangeLogUrl,
  createProjectDashboardUrl,
  createProjectDropsUrl,
  createProjectExpensesUrl,
  createProjectHomeownerNotesUrl,
  createProjectJobLogsUrl,
  createProjectLotSummaryUrl,
  createProjectScheduleUrl,
  createProjectSettingsUrl,
  createProjectToDosUrl,
  createSpecsAndSelectionsUrl,
} from "src/RouteUrls";
import { SideNavLayoutRoute } from "src/routing/components/layouts/SideNavLayoutRoute";

import * as projectPaths from "src/routing/domains/projectsPaths";
import { getProjectTypeIcon } from "src/utils/projects";

/**
 * Project SideNav chrome for the data router: builds nav from project context and renders `<Outlet />`.
 *
 * Role: data router pathless layout. Nest under `ProjectLayout` in `projects.ts`.
 */
export function ProjectSideNavLayout() {
  const project = useProjectContext();
  const sideNavProps = buildProjectSideNavProps(project);
  return <SideNavLayoutRoute sideNavProps={sideNavProps} />;
}

type ProjectNavContext = {
  id: string;
  __typename: string;
  name: string;
  features: ProjectFeature[];
  latestActiveStage: Stage;
  hopUrl: string;
  canHaveBudget: boolean;
  clientContract: string;
  clientInvoice: string;
  shortClientNoun: string;
  buildAddress?: { city?: string | null; state?: string | null; postalCode?: string | null } | null;
};

function buildProjectSideNavProps(project: ReturnType<typeof useProjectContext>): SideNavProps {
  const {
    id,
    __typename,
    name,
    features,
    latestActiveStage,
    hopUrl,
    canHaveBudget,
    clientContract,
    clientInvoice,
    shortClientNoun,
    buildAddress,
  } = project;

  if (!id || !__typename) {
    return { sideNavConfig: [] };
  }

  const navContext: ProjectNavContext = {
    id,
    __typename,
    name,
    features,
    latestActiveStage,
    hopUrl,
    canHaveBudget,
    clientContract,
    clientInvoice,
    shortClientNoun,
    buildAddress,
  };

  if (__typename === "StandaloneProject") {
    return {
      sideNavConfig: standaloneProjectNavLinks(
        { shortClientNoun, clientContract, clientInvoice },
        id,
        latestActiveStage,
        features,
        hopUrl,
      ),
      top: (
        <div css={Css.pb5.$}>
          <CoverImage />
        </div>
      ),
    };
  }

  const { city, state, postalCode } = buildAddress ?? {};
  return {
    sideNavConfig: hierarchyNavLinks(navContext),
    top: (
      <div css={Css.pl2.pt3.pb4.$}>
        <div css={Css.dif.aic.br4.bgGray200.gray600.hPx(40).pl1.pr2.gap1.$}>
          <Icon icon={getProjectTypeIcon({ __typename })} />
          <div css={Css.smSb.$}>{__typename}</div>
        </div>
        <div css={Css.df.fdc.pt1.$}>
          <div css={Css.lg.pr2.ptPx(5).pb1.lhPx(20).$}>{name}</div>
          {buildAddress && (
            <span css={Css.xs.$}>{`${city}, ${state} ${__typename !== "Community" ? postalCode : ""}`}</span>
          )}
        </div>
      </div>
    ),
    displayStyle: SideNavDisplayStyle.Collapsible,
    storagekey: "projectLayout",
  };
}

function standaloneProjectNavLinks(
  lotType: { shortClientNoun: string; clientContract: string; clientInvoice: string },
  projectId: string,
  stage: Stage,
  features: ProjectFeature[] = [],
  hopUrl: string,
): SideNavAndRouterConfig[] {
  return [
    {
      title: "Project",
      items: [
        {
          label: "Dashboard",
          path: projectPaths.projectDashboardRoute.path,
          href: createProjectDashboardUrl(projectId),
        },
        {
          label: "Schedule",
          path: features.includes(ProjectFeature.ProductConfigPlan)
            ? projectPaths.projectDraftScheduleRoute.path
            : projectPaths.projectScheduleRoute.path,
          href: features.includes(ProjectFeature.ProductConfigPlan)
            ? createDraftScheduleUrl(projectId)
            : createProjectScheduleUrl(projectId, stage),
        },
        {
          label: "Job Logs",
          path: `${projectPaths.projectJobLogsRoute.path}/*`,
          href: createProjectJobLogsUrl(projectId),
        },
        { label: "To-Dos", path: `${projectPaths.projectToDosRoute.path}/*`, href: createProjectToDosUrl(projectId) },
        ...(!features.includes(ProjectFeature.ProductConfigPlan)
          ? [
              {
                label: "Pre-Con Services",
                path: projectPaths.projectPreConServicesRoute.path,
                href: createPreConServicesUrl(projectId),
              },
            ]
          : []),
        {
          label: "Lot Summary ",
          path: projectPaths.projectLotSummaryRoute.path,
          href: createProjectLotSummaryUrl(projectId),
        },
        {
          label: "Specs & Selections",
          path: projectPaths.projectSpecsAndSelectionsRoute.path,
          href: createSpecsAndSelectionsUrl(projectId),
        },
        {
          label: "Change Events",
          path: projectPaths.projectChangeEventsRoute.path,
          href: createChangeEventsUrl(projectId),
        },
        {
          label: "Change Log",
          path: `${projectPaths.projectChangeLogRoute.path}/*`,
          href: createProjectChangeLogUrl(projectId),
          featureFlag: FeatureFlagType.ChangeRequestMvp,
        },
        { label: "Documents", path: projectPaths.projectDocumentsRoute.path, href: createDocumentsUrl(projectId) },
        {
          label: `H/O Notes`,
          path: projectPaths.projectHomeownerNotesRoute.path,
          href: createProjectHomeownerNotesUrl(projectId),
        },
      ],
    },
    {
      title: "Finances",
      items: [
        { label: "Budget", path: projectPaths.projectBudgetBaseRoute.path, href: createBudgetUrl(projectId) },
        { label: "Estimates", path: projectPaths.projectEstimatesRoute.path, href: createEstimatesUrl(projectId) },
        {
          label: lotType.clientContract.includes("Contract") ? "Contracts" : `${lotType.clientContract}s`,
          path: projectPaths.projectContractsRoute.path,
          href: createHomeownerContractsUrl(projectId),
        },
        {
          label: `${lotType.clientInvoice}s`,
          path: projectPaths.projectInvoicesRoute.path,
          href: createInvoicesUrl(projectId),
        },
        {
          label: "Trade Commitments",
          path: [projectPaths.projectCommitmentsRoute.path, projectPaths.projectBillOptionalPath],
          href: createCommitmentsUrl(projectId),
        },
        { label: "Trade Expenses", href: createProjectExpensesUrl(projectId) },
      ],
    },
    {
      items: [
        { label: "H/O Platform", href: hopUrl, isExternal: true },
        { label: "Settings", path: projectPaths.projectSettingsRoute.path, href: createProjectSettingsUrl(projectId) },
      ],
    },
  ];
}

type ProjectLayoutItem = Omit<SideNavAndRouterConfigItem, "href"> & { href: (projectId: string) => string };
type ProjectLayoutSection = Omit<SideNavAndRouterConfig, "items"> & { items: ProjectLayoutItem[] };

const allSections: Record<string, ProjectLayoutSection> = {
  dashboard: {
    items: [
      {
        label: "Dashboard",
        path: projectPaths.projectDashboardRoute.path,
        href: (projectId) => createProjectDashboardUrl(projectId),
      },
    ],
  },
  schedule: {
    title: "Schedule",
    items: [
      {
        label: "Schedule",
        path: projectPaths.projectDraftScheduleRoute.path,
        href: (projectId) => createDraftScheduleUrl(projectId),
      },
      {
        label: "Job Logs",
        path: `${projectPaths.projectJobLogsRoute.path}/*`,
        href: (projectId) => createProjectJobLogsUrl(projectId),
      },
    ],
  },
  budgets: {
    title: "Budgets",
    items: [
      {
        label: "Metrics",
        path: projectPaths.projectBudgetMetricsRoute.path,
        href: (projectId) => createBudgetMetricsUrl(projectId),
      },
      {
        label: "Budget",
        path: projectPaths.projectBudgetBaseRoute.path,
        href: (projectId) => createBudgetUrl(projectId),
      },
      {
        label: "POs",
        path: [projectPaths.projectCommitmentsRoute.path, projectPaths.projectBillOptionalPath],
        href: (projectId) => createCommitmentsUrl(projectId),
      },
      {
        label: "Change Events",
        path: projectPaths.projectChangeEventsRoute.path,
        href: (projectId) => createChangeEventsUrl(projectId),
      },
      { label: "Expenses", href: (projectId) => createProjectExpensesUrl(projectId) },
      {
        label: "Estimates",
        path: projectPaths.projectEstimatesRoute.path,
        href: (projectId) => createEstimatesUrl(projectId),
      },
      {
        label: "Client Contracts",
        path: projectPaths.projectContractsRoute.path,
        href: (projectId) => createHomeownerContractsUrl(projectId),
      },
      {
        label: "Client Invoices",
        path: projectPaths.projectInvoicesRoute.path,
        href: (projectId) => createInvoicesUrl(projectId),
      },
    ],
  },
  bids: {
    title: "Bids",
    items: [
      {
        label: `Product Offerings`,
        path: projectPaths.projectProductOfferingsRoute.path,
        href: (projectId) => createProductOfferingsUrl(undefined, projectId),
      },
      {
        label: "Drops",
        path: projectPaths.projectDropsRoute.path,
        href: (projectId) => createProjectDropsUrl(projectId),
      },
      {
        label: `Bid Packages`,
        path: [projectPaths.projectBidPackagesRoute.path, projectPaths.projectBidCoverageRoute.path],
        href: (projectId) => createBidPackagesUrl(projectId),
      },
      {
        label: "Bid Contracts",
        path: projectPaths.projectBidContractsRoute.path,
        href: (projectId) => createProjectBidContractsUrl(projectId),
      },
    ],
  },
  scope: {
    items: [
      {
        label: "Scope",
        path: projectPaths.projectSpecsAndSelectionsRoute.path,
        href: (projectId) => createSpecsAndSelectionsUrl(projectId),
      },
    ],
  },
  lotSummary: { items: [{ label: "Lot Summary", href: (projectId) => createProjectLotSummaryUrl(projectId) }] },
  changeLog: {
    items: [
      {
        label: "Change Log",
        path: `${projectPaths.projectChangeLogRoute.path}/*`,
        href: (projectId) => createProjectChangeLogUrl(projectId),
      },
    ],
  },
  documents: {
    items: [
      {
        label: "Documents",
        path: projectPaths.projectDocumentsRoute.path,
        href: (projectId) => createDocumentsUrl(projectId),
      },
    ],
  },
  settings: {
    pinnedToBottom: true,
    items: [
      {
        label: "Settings",
        path: projectPaths.projectSettingsRoute.path,
        href: (projectId) => createProjectSettingsUrl(projectId),
      },
    ],
  },
};

const { dashboard, schedule, budgets, bids, scope, lotSummary, changeLog, documents, settings } = allSections;
const [directSchedule] = schedule.items;
const [budgetMetrics, budget, commitments, changeEvents, expenses, estimates, clientContracts, clientInvoices] =
  budgets.items;
const [productOfferings, drops, bidPackages, bidContracts] = bids.items;

function hierarchyNavLinks(project: ProjectNavContext): SideNavAndRouterConfig[] {
  const { id, canHaveBudget, hopUrl, __typename } = project;
  let navLinks: ProjectLayoutSection[];

  switch (__typename) {
    case "Region":
      navLinks = [dashboard, documents, settings];
      break;
    case "Market":
      navLinks = [dashboard, { ...bids, items: [bidPackages, bidContracts] }, documents, settings];
      break;
    case "Community":
      navLinks = [
        dashboard,
        { ...schedule, items: [directSchedule] },
        {
          ...budgets,
          items: !canHaveBudget
            ? [{ ...budgetMetrics, label: "Budgets" }]
            : [budgetMetrics, budget, commitments, changeEvents, expenses, estimates, clientContracts],
        },
        { ...bids, items: [productOfferings, drops, bidPackages, bidContracts] },
        ...(!canHaveBudget ? [] : [scope]),
        documents,
        settings,
      ];
      break;

    case "Subdivision":
      navLinks = [
        dashboard,
        { ...schedule, items: [directSchedule] },
        {
          ...budgets,
          items: !canHaveBudget
            ? [{ ...budgetMetrics, label: "Budgets" }]
            : [budgetMetrics, budget, commitments, changeEvents, expenses, estimates, clientContracts],
        },
        { ...bids, items: [productOfferings, drops, bidPackages, bidContracts] },
        ...(!canHaveBudget ? [] : [scope]),
        documents,
        settings,
      ];
      break;

    case "Lot":
      navLinks = [
        dashboard,
        schedule,
        {
          ...budgets,
          items: [
            budgetMetrics,
            budget,
            commitments,
            changeEvents,
            expenses,
            estimates,
            clientContracts,
            clientInvoices,
          ],
        },
        scope,
        changeLog,
        documents,
        settings,
      ];
      break;

    case "Structure":
    case "Unit":
      navLinks = [
        dashboard,
        schedule,
        budgets,
        scope,
        lotSummary,
        changeLog,
        documents,
        {
          title: "Homeowner",
          items: [
            {
              label: "H/O Notes",
              path: projectPaths.projectHomeownerNotesRoute.path,
              href: (projectId) => createProjectHomeownerNotesUrl(projectId),
            },
            { label: "H/O Platform", href: () => hopUrl, isExternal: true },
          ],
        },
        settings,
      ];
      break;

    default:
      throw new Error(`Unexpected hierarchy project type: ${__typename}`);
  }

  return navLinks.map((section) => ({
    ...section,
    items: section.items.map((item) => ({ ...item, href: item.href(id) })),
  }));
}
